import { useState, useEffect, useRef, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

type Difficulty = 'Beginner' | 'Intermediate' | 'Advanced';
type Phase = 'ready' | 'active' | 'solved' | 'timeout';

interface RandomChallengeModalProps {
  onSolve: (query: string) => void;
  onClose: () => void;
}

// ─── Shared practice stats (mirrors PracticeGenerator) ───────────────────────

const PRACTICE_STATS_KEY = 'gm_practice_v1';
type PracticeStats = Record<string, Record<string, number>>;

function loadPracticeStats(): PracticeStats {
  try {
    const raw = localStorage.getItem(PRACTICE_STATS_KEY);
    return raw ? (JSON.parse(raw) as PracticeStats) : {};
  } catch { return {}; }
}

// ─── Problem catalogue (mirrors PracticeGenerator) ───────────────────────────

const ALL_TOPICS = ['Derivative', 'Integral', 'Equation', 'Factor', 'Statistics', 'Limit', 'Simplify', 'Taylor'];
const DIFFICULTIES: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];

const TEMPLATES: Record<string, Record<Difficulty, (string | (() => string))[]>> = {
  Derivative: {
    Beginner: ['Derivative of x^N', 'Derivative of x^N + x^M', 'Derivative of sin(x)', 'Derivative of e^x', 'Derivative of ln(x)'],
    Intermediate: ['Derivative of x^N * sin(x)', 'Derivative of e^x * cos(x)', 'Derivative of ln(x^2 + N)', 'Derivative of x^M * ln(x)'],
    Advanced: ['Derivative of sin(x^N) * ln(x)', 'Derivative of (x^N + 1)/(x - M)', 'Derivative of sqrt(x^2 + N)', 'Derivative of e^(x^N)'],
  },
  Integral: {
    Beginner: ['integrate x^N dx from 0 to M', 'integrate sin(x) dx from 0 to 3', 'integrate e^x dx from 0 to N'],
    Intermediate: ['integrate x * e^x dx from 0 to N', 'integrate sin(x)*cos(x) dx from 0 to 3', 'integrate x^2 * sin(x) dx from 0 to N'],
    Advanced: ['integrate e^(Nx) * cos(x) dx from 0 to M', 'integrate x^N * ln(x) dx from 1 to M', 'integrate sin(x)^2 dx from 0 to 3'],
  },
  Equation: {
    Beginner: ['solve x^2 - Nx + M = 0', 'solve x^2 - N = 0', 'solve 2x + N = M'],
    Intermediate: ['solve x^2 + Nx - M = 0', 'solve x^3 - Nx = 0', 'solve 2x^2 - Nx + M = 0'],
    Advanced: ['solve x^4 - Nx^2 + M = 0', 'solve x^3 - Nx^2 + Mx = 0'],
  },
  Factor: {
    Beginner: ['factor x^2 - Nx + M', 'factor x^2 - N', 'factor x^2 + Nx + M'],
    Intermediate: ['factor x^3 - N', 'factor x^3 + Nx^2', 'factor x^3 - Nx^2 + Mx'],
    Advanced: ['factor x^4 - Nx^2 + M', 'factor x^3 - Nx^2 + Mx - P'],
  },
  Statistics: {
    Beginner: [() => `Mean of {${nums(6)}}`, () => `Median of {${nums(7)}}`],
    Intermediate: [() => `Standard deviation of {${nums(6)}}`, () => `Variance of {${nums(5)}}`],
    Advanced: [() => `Standard deviation of {${nums(10)}}`, () => `Variance of {${nums(8, 1, 50)}}`],
  },
  Limit: {
    Beginner: ['limit as x->N of x^2 - M', 'limit as x->M of 2x + N'],
    Intermediate: ['limit as x->0 of sin(x)/x', 'limit as x->0 of (e^x - 1)/x'],
    Advanced: ['limit as x->0 of (1 - cos(x))/x^2', 'limit as x->0 of x*ln(x)'],
  },
  Simplify: {
    Beginner: ['simplify (x^2 - N)/(x - M)', 'simplify x^N / x^M'],
    Intermediate: ['simplify (x^2 + Nx)/(x)', 'simplify (x^N - M)/(x - P)'],
    Advanced: ['simplify (x^3 - Nx^2 + Mx)/(x)', 'simplify (x^2 - N) * (x + M)'],
  },
  Taylor: {
    Beginner: ['Taylor series of e^x at x=0', 'Taylor series of sin(x) at x=0', 'Taylor series of cos(x) at x=0'],
    Intermediate: ['Taylor series of ln(1+x) at x=0', 'Taylor series of e^(2x) at x=0'],
    Advanced: ['Taylor series of tan(x) at x=0', 'Taylor series of sqrt(1+x) at x=0'],
  },
};

function ri(min: number, max: number) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function nums(c: number, mn = 2, mx = 20) { return Array.from({ length: c }, () => ri(mn, mx)).join(', '); }
function fill(t: string): string {
  return t.replace(/\bN\b/g, () => ri(2, 7).toString()).replace(/\bM\b/g, () => ri(1, 5).toString()).replace(/\bP\b/g, () => ri(2, 9).toString());
}
function genProblem(topic: string, diff: Difficulty): string {
  const pool = TEMPLATES[topic]?.[diff] ?? TEMPLATES['Equation']['Beginner'];
  const tpl = pool[Math.floor(Math.random() * pool.length)];
  return typeof tpl === 'function' ? tpl() : fill(tpl as string);
}

// ─── Weighted random pick ─────────────────────────────────────────────────────

interface Pick { topic: string; difficulty: Difficulty; gapScore: number }

function weightedPick(stats: PracticeStats): Pick {
  const options: (Pick & { weight: number })[] = [];
  for (const topic of ALL_TOPICS) {
    for (const diff of DIFFICULTIES) {
      const count = stats[topic]?.[diff] ?? 0;
      const weight = 1 / (count + 1);          // inverse: fewer attempts → higher weight
      const gapScore = Math.round(weight * 10); // 1-10 indicator of how "weak" this cell is
      options.push({ topic, difficulty: diff, weight, gapScore });
    }
  }
  const total = options.reduce((s, o) => s + o.weight, 0);
  let rand = Math.random() * total;
  for (const o of options) {
    rand -= o.weight;
    if (rand <= 0) return { topic: o.topic, difficulty: o.difficulty, gapScore: o.gapScore };
  }
  return options[0];
}

// ─── Visual helpers ───────────────────────────────────────────────────────────

const TOPIC_ICONS: Record<string, string> = {
  Derivative: 'ri-arrow-up-circle-line',
  Integral: 'ri-function-line',
  Equation: 'ri-equals-line',
  Factor: 'ri-node-tree',
  Statistics: 'ri-bar-chart-2-line',
  Limit: 'ri-arrow-right-line',
  Taylor: 'ri-align-left',
  Simplify: 'ri-scissors-2-line',
};

const DIFF_COLORS: Record<Difficulty, string> = {
  Beginner: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Intermediate: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Advanced: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const TIME_OPTS = [30, 60, 90] as const;

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RandomChallengeModal({ onSolve, onClose }: RandomChallengeModalProps) {
  const [phase, setPhase] = useState<Phase>('ready');
  const [timeLimit, setTimeLimit] = useState<30 | 60 | 90>(60);
  const [timeLeft, setTimeLeft] = useState(60);
  const [solvedInSec, setSolvedInSec] = useState<number | null>(null);
  const [pick, setPick] = useState<Pick>(() => weightedPick(loadPracticeStats()));
  const [problem, setProblem] = useState<string>(() => genProblem(pick.topic, pick.difficulty));

  const startRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup
  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const startTimer = useCallback((limit: number) => {
    setTimeLeft(limit);
    startRef.current = Date.now();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - (startRef.current ?? Date.now())) / 1000;
      const left = Math.max(0, limit - elapsed);
      setTimeLeft(left);
      if (left <= 0) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setPhase('timeout');
      }
    }, 100);
  }, []);

  const handleStart = () => {
    setPhase('active');
    startTimer(timeLimit);
  };

  const handleSolve = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    const elapsed = startRef.current !== null ? (Date.now() - startRef.current) / 1000 : 0;
    setSolvedInSec(Number(elapsed.toFixed(1)));
    setPhase('solved');
    onSolve(problem);
  };

  const handleNext = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    const newPick = weightedPick(loadPracticeStats());
    const newProblem = genProblem(newPick.topic, newPick.difficulty);
    setPick(newPick);
    setProblem(newProblem);
    setPhase('active');
    setSolvedInSec(null);
    startTimer(timeLimit);
  }, [timeLimit, startTimer]);

  const pct = phase === 'active' ? (timeLeft / timeLimit) * 100 : phase === 'ready' ? 100 : 0;
  const timerColor = pct > 50 ? 'text-emerald-500' : pct > 25 ? 'text-amber-500' : 'text-rose-500';
  const barColor = pct > 50 ? 'bg-emerald-500' : pct > 25 ? 'bg-amber-500' : 'bg-rose-500';

  const gapLabel = pick.gapScore >= 8 ? 'Critical gap' : pick.gapScore >= 5 ? 'Weak area' : pick.gapScore >= 3 ? 'Needs work' : 'Solid area';
  const gapColor = pick.gapScore >= 8 ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : pick.gapScore >= 5 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">

        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-dice-line text-white text-xl"></i>
            </div>
            <div>
              <div className="font-bold text-white text-base">Random Challenge</div>
              <div className="text-orange-100 text-xs">Weighted toward your weakest cells</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* ── Timer bar ── */}
        {(phase === 'active' || phase === 'timeout') && (
          <div className="h-2 bg-slate-200 dark:bg-slate-700 w-full flex-shrink-0">
            <div
              className={`h-full transition-all duration-100 ${barColor}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {/* ── Body ── */}
        <div className="p-6 flex-1 overflow-y-auto">

          {/* ── READY phase ── */}
          {phase === 'ready' && (
            <div className="space-y-5">
              {/* Weakness info */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Your Next Challenge</div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${TOPIC_ICONS[pick.topic] ?? 'ri-function-line'} text-white text-base`}></i>
                  </div>
                  <div>
                    <div className="font-bold text-slate-800 dark:text-slate-100 text-base">{pick.topic}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_COLORS[pick.difficulty]}`}>{pick.difficulty}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${gapColor}`}>{gapLabel}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 p-3">
                  <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Your problem</div>
                  <p className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">{problem}</p>
                </div>
              </div>

              {/* Time limit picker */}
              <div>
                <div className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">Time Limit</div>
                <div className="grid grid-cols-3 gap-2">
                  {TIME_OPTS.map(t => (
                    <button
                      key={t}
                      onClick={() => setTimeLimit(t)}
                      className={`py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                        timeLimit === t
                          ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {t}s
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleStart}
                className="w-full py-4 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 hover:opacity-90 text-white font-bold rounded-xl transition-all shadow-lg text-base whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-timer-flash-line text-xl"></i>
                Start {timeLimit}s Challenge
              </button>

              <p className="text-center text-xs text-slate-400 dark:text-slate-500">
                Problem picked based on your heatmap — you&apos;ve practiced {pick.topic} × {pick.difficulty} the least.
              </p>
            </div>
          )}

          {/* ── ACTIVE phase ── */}
          {phase === 'active' && (
            <div className="space-y-5">
              {/* Timer display */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 flex items-center justify-center`}>
                    <i className={`ri-timer-line ${timerColor} text-base`}></i>
                  </div>
                  <span className={`font-black text-2xl tabular-nums ${timerColor}`}>
                    {Math.ceil(timeLeft)}s
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_COLORS[pick.difficulty]}`}>{pick.difficulty}</span>
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{pick.topic}</span>
                </div>
              </div>

              {/* Problem card */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-800/50 rounded-2xl border-2 border-slate-200 dark:border-slate-600 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${TOPIC_ICONS[pick.topic] ?? 'ri-function-line'} text-white text-sm`}></i>
                  </div>
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Solve this problem</span>
                </div>
                <p className="font-mono text-lg font-bold text-slate-900 dark:text-white leading-relaxed">{problem}</p>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleNext()}
                  className="flex items-center justify-center gap-2 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-skip-right-line"></i>
                  Skip
                </button>
                <button
                  onClick={handleSolve}
                  className="flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-rose-500 to-orange-500 hover:opacity-90 text-white rounded-xl text-sm font-bold transition-all shadow-md cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-flashlight-line"></i>
                  Solve It
                </button>
              </div>
            </div>
          )}

          {/* ── SOLVED phase ── */}
          {phase === 'solved' && (
            <div className="space-y-5 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                <i className="ri-check-double-line text-white text-4xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white">Challenge Complete!</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                  You solved it in <span className="font-black text-emerald-600 dark:text-emerald-400 text-base">{solvedInSec}s</span>
                  {timeLimit > 0 && solvedInSec !== null && solvedInSec < timeLimit * 0.5 && (
                    <span className="ml-2 text-amber-500 font-bold">⚡ Lightning fast!</span>
                  )}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-left">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Problem solved</div>
                <p className="font-mono text-sm text-slate-700 dark:text-slate-300">{problem}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_COLORS[pick.difficulty]}`}>{pick.difficulty}</span>
                  <span className="text-xs text-slate-500">{pick.topic}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={onClose} className="py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap">
                  Done
                </button>
                <button onClick={handleNext} className="py-3 bg-gradient-to-r from-rose-500 to-orange-500 hover:opacity-90 text-white rounded-xl text-sm font-bold transition-all shadow-md cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-dice-line"></i>
                  Next Challenge
                </button>
              </div>
            </div>
          )}

          {/* ── TIMEOUT phase ── */}
          {phase === 'timeout' && (
            <div className="space-y-5 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                <i className="ri-alarm-warning-line text-white text-4xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white">Time&apos;s Up!</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                  {timeLimit} seconds gone — don&apos;t worry, practice makes perfect.
                </p>
              </div>
              <div className="bg-rose-50 dark:bg-rose-900/20 rounded-xl border border-rose-200 dark:border-rose-800/50 p-4 text-left">
                <div className="text-[10px] font-bold text-rose-500 uppercase tracking-wide mb-1">Missed problem</div>
                <p className="font-mono text-sm text-slate-700 dark:text-slate-300">{problem}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleSolve}
                  className="py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-flashlight-line"></i>
                  Solve Anyway
                </button>
                <button onClick={handleNext} className="py-3 bg-gradient-to-r from-rose-500 to-orange-500 hover:opacity-90 text-white rounded-xl text-sm font-bold transition-all shadow-md cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-dice-line"></i>
                  Try Another
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
